import { useEffect, useRef, type ElementType, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './lib/cn';

export interface ScrollRevealProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  variant?: 'fade-up' | 'fade' | 'scale' | 'left' | 'right';
  delay?: number;
  as?: ElementType;
  once?: boolean;
}

export function ScrollReveal({
  children,
  className,
  variant = 'fade-up',
  delay,
  as = 'div',
  once = true,
  style,
  ...props
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const Tag = as as any;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      el.classList.add('is-visible');
      return;
    }

    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 100 && rect.bottom > -100) {
      el.classList.add('is-visible');
      if (once) return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            if (once) {
              observer.unobserve(entry.target);
            }
          } else if (!once) {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      {
        threshold: 0.01,
        rootMargin: '100px 0px 100px 0px',
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const variantClass =
    variant === 'fade'
      ? 'reveal-fade'
      : variant === 'scale'
        ? 'reveal-scale'
        : variant === 'left'
          ? 'reveal-left'
          : variant === 'right'
            ? 'reveal-right'
            : 'reveal';

  const combinedStyle = delay ? { ...style, transitionDelay: `${delay}ms` } : style;

  return (
    <Tag
      ref={ref}
      className={cn(variantClass, className)}
      style={combinedStyle}
      {...props}
    >
      {children}
    </Tag>
  );
}

export function initScrollRevealObserver(): () => void {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return () => {};
  }

  const checkElement = (el: Element) => {
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 100 && rect.bottom > -100) {
      el.classList.add('is-visible');
      return true;
    }
    return false;
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.01,
      rootMargin: '100px 0px 100px 0px',
    },
  );

  const observeAll = () => {
    const selectors = [
      '.reveal:not(.is-visible)',
      '.reveal-fade:not(.is-visible)',
      '.reveal-scale:not(.is-visible)',
      '.reveal-left:not(.is-visible)',
      '.reveal-right:not(.is-visible)',
      '[data-reveal]:not(.is-visible)',
    ].join(',');

    document.querySelectorAll(selectors).forEach((el) => {
      if (!checkElement(el)) {
        observer.observe(el);
      }
    });
  };

  observeAll();

  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(() => observeAll());
  }

  const mutationObserver = new MutationObserver(() => {
    observeAll();
  });

  if (document.body) {
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  return () => {
    observer.disconnect();
    mutationObserver.disconnect();
  };
}
